<template>
  <v-container fluid class="fcm-page">
    <div class="d-flex align-center mb-5">
      <v-avatar color="primary" size="40" class="mr-3">
        <v-icon dark>mdi-bell-ring</v-icon>
      </v-avatar>
      <div>
        <div class="text-h6 font-weight-bold">{{ $t("fcm_notifications") }}</div>
        <div class="caption text--secondary">
          {{ toLatin(restaurant?.name) || $t("restaurant_not_selected") }}
        </div>
      </div>
    </div>

    <v-row>
      <v-col cols="12" lg="4">
        <v-card outlined class="fcm-card fill-height">
          <div class="fcm-card__head">
            <v-icon small class="mr-2">mdi-message-text</v-icon>
            {{ $t("message") }}
          </div>
          <div class="pa-4">
            <autocomplete
              :items="projects"
              v-model="notification.project"
              :label="$t('project')"
              item-text="name"
              item-id="code"
              return-object
            >
            </autocomplete>
            <text-field :label="$t('title')" v-model="title"></text-field>
            <text-area :label="$t('message')" v-model="message"></text-area>

            <primary-button
              class="mt-4"
              @click="send"
              :loading="loading"
              :disabled="loading || !withToken || selected.length === 0 || !message"
              :dark="!loading && withToken && !!selected.length && message"
              color="primary"
              height="48px"
              block
            >
              <v-icon class="mr-2">mdi-send</v-icon>
              {{ $t("send") }}
              <span v-if="selected.length" class="ml-1">({{ selected.length }})</span>
            </primary-button>
            <div
              v-if="!loading && (!withToken || !selected.length || !message)"
              class="caption text--secondary mt-2 d-flex align-center"
            >
              <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
              <template v-if="!withToken">{{ $t("fcm_no_token_send_disabled") }}</template>
              <template v-else>
                {{ !selected.length ? $t("fcm_select_users") : $t("fcm_enter_message") }}
              </template>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" lg="8">
        <v-card outlined class="fcm-card fill-height">
          <div class="fcm-card__head">
            <v-icon small class="mr-2">mdi-filter-variant</v-icon>
            {{ $t("filters") }}
          </div>
          <div class="pa-4">
            <v-row dense align="end">
              <v-col cols="12" sm="auto" v-if="isAdmin">
                <v-btn-toggle v-model="row" mandatory color="primary" class="fcm-toggle">
                  <v-btn value="restaurant">
                    <v-icon small left>mdi-silverware-fork-knife</v-icon>
                    {{ $t("restaurant") }}
                  </v-btn>
                  <v-btn value="company">
                    <v-icon small left>mdi-domain</v-icon>
                    {{ $t("companies") }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
              <v-col cols="12" sm>
                <dropdown
                  :label="$t('view')"
                  :items="showItems"
                  v-model="selectedLayout"
                  hide-details
                  outlined
                  dense
                ></dropdown>
              </v-col>
            </v-row>

            <template v-if="row === 'company'">
              <v-row dense class="mt-3">
                <v-col cols="12">
                  <autocomplete
                    :items="companies"
                    v-model="selectedCompanies"
                    select-all
                    multiple
                    hide-details
                    :label="$t('company')"
                    item-text="name"
                    item-id="id"
                    return-object
                  ></autocomplete>
                </v-col>
              </v-row>
              <v-row dense class="mt-3">
                <v-col cols="12" md="6">
                  <dropdown
                    :items="notificationTypes"
                    v-model="selectedNotificationType"
                    :label="'Tip notifikacije'"
                    item-text="text"
                    item-value="value"
                    outlined
                    dense
                    hide-details
                  ></dropdown>
                </v-col>
                <v-col cols="12" md="6">
                  <date-picker
                    v-model="date"
                    single-date
                    :label="getDateLabel()"
                    hide-details
                    outlined
                    dense
                  ></date-picker>
                </v-col>
              </v-row>
            </template>

            <v-divider class="my-4"></v-divider>

            <div class="d-flex align-center flex-wrap">
              <v-switch
                v-model="withToken"
                :label="$t('fcm_only_with_token')"
                color="primary"
                inset
                hide-details
                class="mt-0 pt-0"
              ></v-switch>
              <v-spacer></v-spacer>
              <primary-button v-if="row === 'company'" @click="getUsers" height="40px">
                <v-icon small class="mr-1">mdi-magnify</v-icon>
                {{ $t("search") }}
              </primary-button>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="fcm-card mt-6">
      <div class="fcm-card__head d-flex align-center flex-wrap">
        <v-icon small class="mr-2">mdi-account-multiple</v-icon>
        {{ $t("users") }}
        <v-chip small class="ml-3" color="primary" text-color="white">
          {{ selected.length }} / {{ users.length }} {{ $t("selected") }}
        </v-chip>
        <v-chip v-if="withoutTokenCount" small class="ml-2" color="grey lighten-3" text-color="grey darken-2">
          <v-icon left small>mdi-cellphone-off</v-icon>
          {{ withoutTokenCount }} {{ $t("not_found_token") }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="tableSearch"
          :placeholder="$t('search')"
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          hide-details
          class="fcm-search"
        ></v-text-field>
      </div>

      <div class="pa-4 pb-0">
        <p v-if="count_orders" class="d-flex justify-space-between">
          <span class="font-weight-bold">{{
            $t("ordered_qty").toUpperCase() + ": " + count_orders
          }}</span>
          <span class="font-weight-bold">{{
            ($t("no") + " " + $t("ordered_qty")).toUpperCase() + ": " + users.length
          }}</span>
        </p>
        <v-row dense>
          <v-col v-for="tile in statusTiles" :key="tile.state" cols="6" sm="4" md>
            <v-card
              outlined
              class="fcm-tile"
              :class="{ 'fcm-tile--active': statusFilter === tile.state }"
              :style="{ borderLeftColor: tile.borderColor }"
              @click="toggleStatusFilter(tile.state)"
            >
              <div class="d-flex align-center pa-3">
                <v-icon :color="tile.color" class="mr-3">{{ tile.icon }}</v-icon>
                <div>
                  <div class="text-h5 font-weight-bold">{{ tile.count }}</div>
                  <div class="caption text--secondary">{{ tile.label }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="filteredUsers"
        :search="tableSearch"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
        class="striped-table mt-3"
        item-key="id"
        show-select
        :loading="loading"
        :loading-text="$t('loading_data')"
      >
        <template v-slot:item.num="{ index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.messageStatus="{ item }">
          <div class="py-2">
            <v-chip
              small
              label
              :color="statusStyle(item.messageState).color"
              :text-color="statusStyle(item.messageState).text"
            >
              <v-icon left small>{{ statusStyle(item.messageState).icon }}</v-icon>
              {{ item.messageStatus || $t("pending") }}
            </v-chip>
            <div v-if="item.messageReason" class="caption text--secondary mt-1">
              {{ item.messageReason }}
            </div>
            <div
              v-else-if="!item.messageState && item.devices_count === 0"
              class="caption text--secondary mt-1 d-flex align-center"
            >
              <v-icon x-small class="mr-1">mdi-cellphone-off</v-icon>
              {{ $t("not_found_token") }}
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment/moment";
import companyService from "@/services/company";
import orderService from "@/services/order";
import notificationService from "@/services/notification";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";

export default {
  mixins: [hasSnackbar, warehouseFiltersMixin],
  data() {
    return {
      selected: [],
      date: "",
      date1: "",
      selectedNotificationType: "",
      notificationTypes: [
        { text: this.$t("all_users"), value: "" },
        { text: "Nije naručio na vrijeme", value: "filter-not-ordered-on-time" },
        { text: "Nije preuzeo narudžbu", value: "filter-not-picked-up" },
        { text: "Preuzeo rezervni obrok", value: "filter-used-resrved" },
        { text: "Naručio obrok", value: "filter-ordered" },
      ],
      menu: false,
      menu1: false,
      notification: {
        project: { code: "ordera-e6eb8", name: "Ordera" },
      },
      projects: [
        { code: "ordera-e6eb8", name: "Ordera" },
        { code: "order-notification-8f5f2", name: "Restorani" },
      ],
      headers: [],
      users: [],
      originalUsers: [],
      loading: false,
      info: {
        state: false,
        list: [],
      },
      title: "",
      message: "",
      companies: [],
      selectedCompanies: [],
      row: "company",
      firm_users: false,
      no_order_on_time_users: false,
      orders_not_taken: false,
      count_orders: null,
      selectedLayout: { text: this.$t("meals_in_meals"), value: "daily" },
      statusFilter: null,
      tableSearch: "",
      withToken: true,
    };
  },
  created() {
    this.headers = [
      { text: this.$t("r_b"), value: "num" },
      {
        text: this.$t("name"),
        align: "start",
        sortable: false,
        value: "name",
      },
      { text: this.$t("lastname"), value: "lastname" },
      { text: this.$t("email"), value: "email" },
      { text: this.$t("type"), value: "type" },
      { text: this.$t("state"), value: "messageStatus" },
    ];
    this.getUsers();
    this.loadCompanies();
  },
  methods: {
    getDateLabel() {
      if (this.selectedNotificationType === "not-ordered-on-time") {
        return this.$t("not_ordered_on_time") || "Nije naručio na vrijeme";
      } else if (this.selectedNotificationType === "not-picked-up") {
        return this.$t("not_picked_up") || "Nije preuzeo narudžbu";
      }
      return this.$t("date") || "Datum";
    },
    toogleAllCompanies() {
      if (this.selectedCompanies.length > 0) {
        this.selectedCompanies = [];
        this.getUsers();
      } else if (this.selectedCompanies.length == 0) {
        this.selectedCompanies = [];
        // for(let company in this.report_food_original) {
        //   this.searchFoodCompany.push(company)
        // }
        this.companies.filter((com) => {
          this.selectedCompanies.push(com);
        });
        this.getUsers();
      }
      this.$forceUpdate();
    },
    selectAllFirmUsers() {
      if (this.firm_users) {
        this.users = this.originalUsers;
        this.date = "";
        this.date1 = "";
        this.count_orders = null;
      }
    },
    loadNotTakenOrders() {
      if (this.selectedCompanies.length) {
        let firme = [];
        let korisnikId = [];
        this.selectedCompanies.filter((item) => {
          firme.push(item.id);
        });
        this.users.filter((user) => {
          korisnikId.push(user.id);
        });
        orderService
          .filterNotTakenOrders({
            companies: firme,
            date: this.date,
            users: korisnikId,
          })
          .then(({ data }) => {
            this.users = data.no_order;
            this.firm_users = false;
          })
          .catch(() => {
            this.showErrorSnackbar(this.$t("error_loading_not_taken_orders"));
          });
      }
    },
    loadOrdersTime() {
      if (this.selectedCompanies.length) {
        let firme = [];
        let korisnikId = [];
        this.selectedCompanies.filter((item) => {
          firme.push(item.id);
        });
        this.users.filter((user) => {
          korisnikId.push(user.id);
        });
        orderService
          .filterNotOnTimeOrders({
            companies: firme,
            date: this.date,
            users: korisnikId,
          })
          .then(({ data }) => {
            this.users = data.no_order;
            this.count_orders = data.order;
            this.firm_users = false;
          })
          .catch(() => {
            this.showErrorSnackbar(this.$t("error_loading_orders_time"));
          });
      }
    },
    loadCompanies() {
      this.companies = [];
      companyService
        .getCompaniesWithRestaurants()
        .then((res) => {
          this.companies = (res.data || []).reduce((companies, company) => {
            companies.push(company);
            if ((company.children || []).length) {
              companies = companies.concat(company.children);
            }
            return companies;
          }, []);
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_companies"));
        });
      this.$forceUpdate();
    },
    getUsers() {
      this.loading = true;
      this.statusFilter = null;

      const params = {
        project: this.notification.project.code,
        row: this.row,
        type: this.selectedLayout.value === "daily" ? "meal" : "group",
      };

      if (this.row === "restaurant") {
        params.restaurant_ids = this.restaurant.id;
      } else {
        params.company_ids = this.selectedCompanies.map((i) => i.id).join(",");
      }

      params.has_token = this.withToken ? 1 : 0;
      params.date = this.date;
      if (this.selectedNotificationType) params[this.selectedNotificationType] = true;

      notificationService
        .getUsers(params)
        .then((res) => {
          this.users = res.data;
          this.originalUsers = res.data;
        })
        .finally(() => {
          this.loading = false;
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_users"));
        });
    },
    // Jedan korisnik moze imati vise uredjaja - dovoljno je da stigne na jedan
    fcmStatus(results) {
      if (!results.length) {
        return { messageState: "no_token", messageStatus: this.$t("not_found_token"), messageReason: "" };
      }

      const sent = results.filter((i) => i.response?.name).length;
      if (sent === results.length) {
        return { messageState: "ok", messageStatus: this.$t("ok"), messageReason: "" };
      }
      if (sent) {
        return {
          messageState: "partial",
          messageStatus: `${this.$t("fcm_partial")} ${sent}/${results.length}`,
          messageReason: "",
        };
      }

      const errors = results.map((i) => i.response?.error || {});
      let reason = errors.map((e) => e.message).find(Boolean) || "";
      if (results.every((i) => i.removed_device)) {
        reason = this.$t("fcm_device_removed");
      } else if (errors.some((e) => e.status === "PERMISSION_DENIED")) {
        reason = this.$t("fcm_wrong_project");
      }
      return { messageState: "failed", messageStatus: this.$t("failed"), messageReason: reason };
    },
    statusStyle(state) {
      return (
        {
          ok: { color: "green lighten-5", text: "green darken-3", icon: "mdi-check-circle" },
          partial: { color: "orange lighten-5", text: "orange darken-4", icon: "mdi-alert-circle" },
          failed: { color: "red lighten-5", text: "red darken-3", icon: "mdi-close-circle" },
          no_token: { color: "grey lighten-3", text: "grey darken-2", icon: "mdi-cellphone-off" },
        }[state] || { color: "grey lighten-4", text: "grey darken-1", icon: "mdi-clock-outline" }
      );
    },
    toggleStatusFilter(state) {
      this.statusFilter = this.statusFilter === state ? null : state;
    },
    send() {
      this.loading = true;
      notificationService
        .sendFcm({
          project: this.notification.project.code,
          user_ids: this.selected.map((user) => user.id),
          title: this.title,
          body: this.message,
        })
        .then((res) => {
          this.info.list = res.data;
          const sentTo = this.selected.map((user) => user.id);
          // Status menjamo samo korisnicima kojima je sada poslato
          this.users = this.users.map((user) => {
            if (!sentTo.includes(user.id)) return user;
            const results = this.info.list.filter((i) => i.user_id === user.id);
            return { ...user, ...this.fcmStatus(results) };
          });
          this.showSuccessSnackbar(this.$t("notifications_sent"));
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_sending_notifications"));
        })
        .finally(() => {
          this.loading = false;
          this.info.state = true;
        });
    },
  },
  computed: {
    withoutTokenCount() {
      return this.users.filter((u) => u.devices_count === 0).length;
    },
    filteredUsers() {
      if (!this.statusFilter) return this.users;
      if (this.statusFilter === "pending") return this.users.filter((u) => !u.messageState);
      return this.users.filter((u) => u.messageState === this.statusFilter);
    },
    statusTiles() {
      const count = (state) =>
        this.users.filter((u) => (u.messageState || "pending") === state).length;
      return [
        { state: "pending", label: this.$t("pending"), icon: "mdi-clock-outline", color: "blue-grey", borderColor: "#78909C" },
        { state: "ok", label: this.$t("ok"), icon: "mdi-check-circle", color: "green darken-1", borderColor: "#43A047" },
        { state: "partial", label: this.$t("fcm_partial"), icon: "mdi-alert-circle", color: "orange darken-2", borderColor: "#F57C00" },
        { state: "failed", label: this.$t("failed"), icon: "mdi-close-circle", color: "red darken-1", borderColor: "#E53935" },
        { state: "no_token", label: this.$t("not_found_token"), icon: "mdi-cellphone-off", color: "grey darken-1", borderColor: "#757575" },
      ].map((tile) => ({ ...tile, count: count(tile.state) }));
    },
    isAdmin() {
      return this.$store.state.user?.user?.type === "admin";
    },
    dateRangeText() {
      if (this.date.length == 1 || this.date[0] == this.date[1]) {
        return moment(this.date[0]).format("DD.MM.YYYY");
      }
      return (
        moment(this.date[0]).format("DD.MM.YYYY") +
        " - " +
        moment(this.date[1]).format("DD.MM.YYYY")
      );
    },
    restaurant() {
      return this.$store.state.restaurant;
    },
  },
  watch: {
    restaurant() {
      this.getUsers();
      this.loadCompanies();
    },
    "notification.project"() {
      this.getUsers();
    },
    selectedCompanies() {
      this.getUsers();
    },
    row() {
      this.getUsers();
    },
    date() {
      if (this.selectedNotificationType === "not-ordered-on-time") {
        //this.loadOrdersTime();
      } else if (this.selectedNotificationType === "not-picked-up") {
        //this.loadNotTakenOrders();
      }

      this.getUsers();
    },
    selectedNotificationType() {
      if (this.selectedNotificationType === "not-ordered-on-time") {
        //this.loadOrdersTime();
      } else if (this.selectedNotificationType === "not-picked-up") {
        //this.loadNotTakenOrders();
      }

      this.getUsers();
    },
    selectedLayout() {
      this.getUsers();
    },
    withToken() {
      this.getUsers();
    },
  },
};
</script>

<style scoped>
.fcm-card {
  border-radius: 10px !important;
}
.fcm-card__head {
  padding: 12px 16px;
  font-weight: 600;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background-color: rgba(0, 0, 0, 0.02);
}
.fcm-search {
  max-width: 280px;
}
.fcm-toggle .v-btn {
  height: 40px !important;
  text-transform: none;
}
.fcm-tile {
  border-left-width: 4px !important;
  cursor: pointer;
  transition: box-shadow 0.15s;
}
.fcm-tile:hover,
.fcm-tile--active {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}
.fcm-tile--active {
  background-color: rgba(0, 0, 0, 0.03);
}
</style>
